import { useState, useEffect, useCallback } from "react";
import { useCars } from "../../../Context/CarContext";
import { getVehicleServices } from "../../../services/api/serviceApi";
import { sortServicesDesc } from "../../LatestServices/components/jalaliUtils";

const PREVIEW_LIMIT = 3;

function toPreviewList(raw, limit = PREVIEW_LIMIT) {
  if (!raw) return [];
  const merged = [
    ...(raw.periodicKmTime || []).map((s) => ({
      ...s,
      fallbackType: "دوره‌ای (کیلومتر/زمان)",
    })),
    ...(raw.actionTime || []).map((s) => ({
      ...s,
      fallbackType: "دوره‌ای (عملکرد)",
    })),
    ...(raw.repair || []).map((s) => ({
      ...s,
      fallbackType: "تعویض / تعمیر",
    })),
  ];
  const enriched = merged.map((s) => ({
    ...s,
    displayType: s.category?.title || s.fallbackType,
  }));
  return sortServicesDesc(enriched).slice(0, limit);
}

/**
 * Data hook for the dashboard "Latest Services" preview.
 * Reuses the exact same backend source as the /LatestServices page
 * (GET /service/vehicle/:vehicleId for the selected car) and shows
 * only the newest `limit` services. No static/fake data.
 */
export function useLatestServicesPreview(limit = PREVIEW_LIMIT) {
  const { getSelectedCar } = useCars();
  const selectedCar = getSelectedCar();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!selectedCar) {
      setServices([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await getVehicleServices(selectedCar.id);
      if (response?.success && response?.data) {
        setServices(toPreviewList(response.data, limit));
      } else {
        setError(response?.message || "خطا در دریافت سرویس‌ها");
        setServices([]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "خطای شبکه"
      );
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCar?.id, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  return { services, loading, error, reload: load, selectedCar };
}

export default useLatestServicesPreview;
